// react imports
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { InputNumber, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import axios from "axios";

// common imports
import { API_URL } from "common/constants/config";

import { NATURAL_GAS_PROVIDERS_NAMES } from "./constants/energies";
import CategoryHeader from "./CategoryHeader";
import "./css/Pagination.css";
import "./css/Table.css";

function Energies() {
  type NaturalGasProvider =
    | "EDP"
    | "endesa"
    | "government"
    | "iberdrola"
    | "naturgy"
    | "repsol";

  interface NaturalGasInputs {
    [provider: string]: {
      fixedTerMonthly: number;
      variableTermKwh: number;
    };
  }

  interface DieselInputs {
    costPerLitre: number;
  }

  interface Inputs {
    diesel: DieselInputs;
    naturalGas: {
      providers: NaturalGasInputs;
    };
  }

  interface NaturalGasData {
    key: string;
    provider: string;
    contractName: string;
    fixedTermMonthly: number;
    variableTermKwh: number;
  }

  interface DieselData {
    key: string;
    costPerLitre: number;
  }

  const [naturalGasResponse, setNaturalGasResponse] =
    useState<Inputs["naturalGas"]>();
  const [dieselResponse, setDieselResponse] = useState({});
  const [naturalGas, setNaturalGas] = useState<NaturalGasData[]>([]);
  const [diesel, setDiesel] = useState<DieselData[]>([]);
  const { control, handleSubmit } = useForm<Inputs>();

  const naturalGasColumns: ColumnsType<NaturalGasData> = [
    {
      title: "Tipo de energía",
      key: "energyType",
      dataIndex: "energyType",
      align: "center",
      render: () => "Gas natural",
    },
    {
      title: "Empresa",
      key: "provider",
      dataIndex: "provider",
      align: "center",
    },
    {
      title: "Plan",
      key: "contractName",
      dataIndex: "contractName",
      align: "center",
    },
    {
      title: "Termino fijo/mes",
      key: "fixedTermMonthly",
      dataIndex: "fixedTermMonthly",
      align: "center",
      render: (value, record) => (
        <Controller
          name={`naturalGas.providers.${record.key}.fixedTermMonthly`}
          control={control}
          defaultValue={value}
          render={({ field }) => (
            <InputNumber
              {...field}
              decimalSeparator=","
              onPressEnter={handlePressEnter}
            />
          )}
        />
      ),
    },
    {
      title: "€/Kwh",
      key: "variableTermKwh",
      dataIndex: "variableTermKwh",
      align: "center",
      render: (value, record) => (
        <Controller
          name={`naturalGas.providers.${record.key}.variableTermKwh`}
          control={control}
          defaultValue={value}
          render={({ field }) => (
            <InputNumber
              {...field}
              decimalSeparator=","
              onPressEnter={handlePressEnter}
            />
          )}
        />
      ),
    },
  ];

  const dieselColumns: ColumnsType<DieselData> = [
    {
      title: "Tipo de energía",
      key: "energyType",
      dataIndex: "energyType",
      align: "center",
      width: 209,
      render: () => "Gasóleo",
    },
    {
      title: "€/l",
      key: "costPerLitre",
      dataIndex: "costPerLitre",
      align: "center",
      width: 190,
      render: (value, record) => (
        <Controller
          name="diesel.costPerLitre"
          control={control}
          defaultValue={value}
          render={({ field }) => (
            <InputNumber
              {...field}
              decimalSeparator=","
              onPressEnter={handlePressEnter}
            />
          )}
        />
      ),
    },
  ];

  function handlePressEnter(e: React.KeyboardEvent<HTMLInputElement>) {
    e.preventDefault();
  }

  async function onSubmit(data: Inputs) {
    const { providers } = naturalGasResponse as Inputs["naturalGas"];
    for (const provider in providers) {
      if (data.naturalGas.providers[provider]) {
        Object.assign(providers[provider], data.naturalGas.providers[provider]);
      }
    }
    const dieselValues = Object.assign({}, dieselResponse);
    Object.assign(dieselValues, data.diesel);
    const payload = {
      naturalGas: {
        ...naturalGasResponse,
        providers,
      },
      diesel: dieselValues,
    };

    await axios.put("/admin/calculator/energy", payload, {
      baseURL: API_URL,
    });
  }

  async function fetchEnergy() {
    const {
      data: {
        energy: { diesel: dieselValues, naturalGas: naturalGasValues },
      },
    } = await axios.get(`${API_URL}/admin/calculator/energy`);

    const naturalGasProviders = Object.entries<Omit<NaturalGasData, "key">>(
      naturalGasValues.providers
    ).map(([provider, values]) => {
      return {
        ...values,
        key: provider,
        provider: NATURAL_GAS_PROVIDERS_NAMES[provider as NaturalGasProvider],
      };
    });

    setNaturalGasResponse(naturalGasValues);
    setNaturalGas(naturalGasProviders);
    setDieselResponse(dieselValues);
    setDiesel([
      {
        key: "diesel",
        costPerLitre: dieselValues.costPerLitre,
      },
    ]);
  }

  useEffect(() => {
    fetchEnergy();
  }, []); // eslint-disable-line

  return (
    <>
      <CategoryHeader title="Energías" formName="energies" />
      <form id="energies" onSubmit={handleSubmit(onSubmit)}>
        <Table
          columns={naturalGasColumns}
          dataSource={naturalGas}
          pagination={false}
        />
        <Table columns={dieselColumns} dataSource={diesel} pagination={false} />
      </form>
    </>
  );
}

export default Energies;
